import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

interface VascularAccessRecord {
  site: string
  condition: string
  ivSolution: string
  rate: string
}

interface VascularAccessDialogueProps {
  vascularAccessRecords: VascularAccessRecord[]
  setVascularAccessRecords: (records: VascularAccessRecord[]) => void
  open: boolean
  onOpen: () => void
  onClose: () => void
  isDisabled?: boolean
}

const VascularAccessDialogue: React.FC<VascularAccessDialogueProps> = ({
  vascularAccessRecords,
  setVascularAccessRecords,
  open,
  onOpen,
  onClose,
  isDisabled = false,
}) => {
  const [newRecord, setNewRecord] = useState<VascularAccessRecord>({
    site: '',
    condition: '',
    ivSolution: '',
    rate: '',
  })
  const [showWarning, setShowWarning] = useState(false)

  const handleAddRecord = () => {
    if (!newRecord.site || !newRecord.condition || !newRecord.ivSolution || !newRecord.rate) {
      setShowWarning(true)
      return
    }
    setVascularAccessRecords([...vascularAccessRecords, newRecord])
    setNewRecord({
      site: '',
      condition: '',
      ivSolution: '',
      rate: '',
    })
    onClose()
  }

  const handleDeleteRecord = (index: number) => {
    const updated = vascularAccessRecords.filter((_, i) => i !== index)
    setVascularAccessRecords(updated)
  }

  const handleInputChange = (field: keyof VascularAccessRecord, value: any) => {
    setNewRecord((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCloseWarning = () => {
    setShowWarning(false)
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{ backgroundColor: '#2f3347' }}>
            <TableRow>
              <TableCell style={{ color: 'white' }}>Actions</TableCell>
              <TableCell style={{ color: 'white' }}>Site/Type</TableCell>
              <TableCell style={{ color: 'white' }}>Condition</TableCell>
              <TableCell style={{ color: 'white' }}>IV Solution</TableCell>
              <TableCell style={{ color: 'white' }}>Rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vascularAccessRecords.length > 0 ? (
              vascularAccessRecords.map((record, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <IconButton 
                      onClick={() => handleDeleteRecord(index)}
                      disabled={isDisabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>{record.site}</TableCell>
                  <TableCell>{record.condition}</TableCell>
                  <TableCell>{record.ivSolution}</TableCell>
                  <TableCell>{record.rate}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align='center' style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  No Records Added
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button 
        variant='contained' 
        onClick={onOpen} 
        startIcon={<AddIcon />} 
        sx={{ mt: 2 }}
        disabled={isDisabled}
      >
        Add Vascular Access
      </Button>

      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>Add Vascular Access</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label='Site/Type'
            fullWidth
            value={newRecord.site}
            onChange={(e) => handleInputChange('site', e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label='Condition'
            fullWidth
            value={newRecord.condition}
            onChange={(e) => handleInputChange('condition', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label='IV Solution'
            fullWidth
            value={newRecord.ivSolution}
            onChange={(e) => handleInputChange('ivSolution', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label='Rate'
            fullWidth
            value={newRecord.rate}
            onChange={(e) => handleInputChange('rate', e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant='contained' onClick={handleAddRecord}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showWarning}
        autoHideDuration={4000}
        onClose={handleCloseWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseWarning} severity='warning' sx={{ width: '100%' }}>
          Please fill in all required fields (Site/Type, Condition, IV Solution, Rate).
        </Alert>
      </Snackbar>
    </>
  )
}

export default VascularAccessDialogue 